import { Button } from "@/components/ui/button";
import DatePickerInput from "@/components/ui/date-picker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SelectContent, SelectItem, SelectTrigger, SelectValue, Select, SelectRemove } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { PUBLIC_AQUISITION_STATUS, PUBLIC_AQUISITIONS_DIRECT, PUBLIC_AQUISITIONS_OFFLINE, PUBLIC_AQUISITIONS_PUBLIC, PUBLIC_AQUISITIONS_TYPE } from "@ept/shared";
import { Filter } from "lucide-react";
import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";
import { useTranslations } from "use-intl";
type LocaleNumberInputProps = {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder: string;
};
const LocaleNumberInput = ({
  value,
  onChange,
  placeholder
}: LocaleNumberInputProps) => {
  const t = useTranslations();
  const [displayValue, setDisplayValue] = useState("");
  useEffect(() => {
    setDisplayValue(value !== undefined ? new Intl.NumberFormat().format(value) : "");
  }, [value]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d.-]/g, "");
    onChange(rawValue ? Number(rawValue) : undefined);
  };
  const handleClear = () => {
    onChange(undefined);
  };
  return <div className="relative w-full" data-sentry-component="LocaleNumberInput" data-sentry-source-file="PublicAquisitionsFilters.tsx">
      <Input className={cn("rounded-xl pr-12", value !== undefined && "pl-10")} containerClassName="w-full" placeholder={placeholder} value={displayValue} onChange={handleChange} data-sentry-element="Input" data-sentry-source-file="PublicAquisitionsFilters.tsx" />
      {value !== undefined && <Button variant="link" onClick={handleClear} className="absolute left-0 top-1/2 ml-2 -translate-y-1/2 transform px-1">
          <X className="h-4 w-4 opacity-50 transition-all hover:opacity-90"></X>
        </Button>}
      <span className="absolute right-3 top-1/2 -translate-y-1/2 transform text-muted-foreground">
        {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_0")}
      </span>
    </div>;
};
type PublicAquisitionsFiltersProps = {
  minDate: Date | undefined;
  setMinDate: (date: Date | undefined) => void;
  maxDate: Date | undefined;
  setMaxDate: (date: Date | undefined) => void;
  minValue: number | undefined;
  setMinValue: (value: number | undefined) => void;
  maxValue: number | undefined;
  setMaxValue: (value: number | undefined) => void;
  type: (typeof PUBLIC_AQUISITIONS_TYPE)[number] | undefined;
  setType: (type: (typeof PUBLIC_AQUISITIONS_TYPE)[number] | undefined) => void;
  procedureType: string | undefined;
  setProcedureType: (procedureType: string | undefined) => void | undefined;
  searchKeywords: string | undefined;
  setSearchKeywords: (keywords: string) => void | undefined;
};
const PublicAquisitionsFilters = ({
  minDate,
  setMinDate,
  maxDate,
  setMaxDate,
  minValue,
  setMinValue,
  maxValue,
  setMaxValue,
  type,
  setType,
  procedureType,
  setProcedureType,
  searchKeywords,
  setSearchKeywords
}: PublicAquisitionsFiltersProps) => {
  const t = useTranslations();
  const [filterCount, setFilterCount] = useState(0);
  const [open, setOpen] = useState(false);
  const i18nStatus = useTranslations("enums.PUBLIC_AQUISITION_STATUS");
  const i18nType = useTranslations("enums.PUBLIC_AQUISITIONS_TYPE");
  const [filters, setFilters] = useState<Omit<PublicAquisitionsFiltersProps, "setMinDate" | "setMaxDate" | "setMinValue" | "setMaxValue" | "setType" | "setProcedureType" | "setSearchKeywords" | "searchKeywords">>({
    minDate,
    maxDate,
    minValue,
    maxValue,
    type,
    procedureType
  });
  useEffect(() => {
    const count = (filters.minDate || filters.maxDate ? 1 : 0) + (filters.minValue || filters.maxValue ? 1 : 0) + (filters.procedureType ? 1 : 0) + (filters.type ? 1 : 0);
    setFilterCount(count);
  }, [filters.minDate, filters.maxDate, filters.minValue, filters.maxValue, filters.procedureType, filters.type]);
  const onSave = () => {
    setMinDate(filters.minDate);
    setMaxDate(filters.maxDate);
    setMinValue(filters.minValue);
    setMaxValue(filters.maxValue);
    setProcedureType(filters.procedureType);
    setType(filters.type);
    setOpen(false);
  };
  const onDiscard = () => {
    setFilters({
      minDate,
      maxDate,
      minValue,
      maxValue,
      procedureType,
      type
    });
  };
  const appliedFilters = () => {
    let appliedFilters = [];
    if (filters.minDate && !filters.maxDate) {
      appliedFilters.push(`Data de la: ${filters.minDate.toLocaleDateString()}`);
    }
    if (!filters.minDate && filters.maxDate) {
      appliedFilters.push(`Data pana la: ${filters.maxDate.toLocaleDateString()}`);
    }
    if (filters.minDate && filters.maxDate) {
      appliedFilters.push(`Data: ${filters.minDate.toLocaleDateString()} - ${filters.maxDate.toLocaleDateString()}`);
    }
    if (filters.minValue) appliedFilters.push(`Valoare de la: ${new Intl.NumberFormat().format(filters.minValue)} RON`);
    if (filters.maxValue) appliedFilters.push(`Valoare pana la: ${new Intl.NumberFormat().format(filters.maxValue)} RON`);
    if (filters.procedureType) appliedFilters.push(`Statusul achizitiei: ${filters.procedureType}`);
    if (filters.type) appliedFilters.push(`Categoria achizitiei: ${filters.type}`);
    return appliedFilters.join(", ");
  };
  return <div className="relative flex w-full items-center justify-end gap-2" data-sentry-component="PublicAquisitionsFilters" data-sentry-source-file="PublicAquisitionsFilters.tsx">
      <div className="relative">
        <Dialog open={open} onOpenChange={open => {
        setOpen(open);
        if (!open) {
          onDiscard();
        }
      }} data-sentry-element="Dialog" data-sentry-source-file="PublicAquisitionsFilters.tsx">
          <DialogTrigger asChild data-sentry-element="DialogTrigger" data-sentry-source-file="PublicAquisitionsFilters.tsx">
            <Button className="group relative h-10 w-10 overflow-visible rounded-full p-0 ring-0 focus:ring-0" data-sentry-element="Button" data-sentry-source-file="PublicAquisitionsFilters.tsx">
              <Filter className="h-4 w-4" data-sentry-element="Filter" data-sentry-source-file="PublicAquisitionsFilters.tsx" />
              {filterCount > 0 && <div className="absolute right-0 top-0 -mr-2 -mt-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                  {filterCount}
                </div>}
              <div className="pointer-events-none absolute left-full top-0 ml-2 hidden min-h-full w-auto flex-col items-start justify-center whitespace-nowrap rounded-3xl bg-blue-700/90 p-2 px-4 text-xs text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100 desktop:flex">
                {appliedFilters() || "No filters applied"}
              </div>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[90vw] desktop:max-w-md" data-sentry-element="DialogContent" data-sentry-source-file="PublicAquisitionsFilters.tsx">
            <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="PublicAquisitionsFilters.tsx">
              <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_1")}
              </DialogTitle>
            </DialogHeader>
            <div className="-mx-3 mt-4 flex flex-col gap-4 overflow-auto px-3 pb-1 text-sm">
              <Card className="flex flex-col gap-4 rounded-3xl p-6" data-sentry-element="Card" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                <span>
                  {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_2")}
                </span>
                <div className="flex flex-col gap-4 desktop:flex-row">
                  <DatePickerInput value={filters.minDate} onValueChange={date => setFilters(filters => ({
                  ...filters,
                  minDate: date
                }))} label={t("label_dateFrom_20230807145097")} className="w-full rounded-xl" data-sentry-element="DatePickerInput" data-sentry-source-file="PublicAquisitionsFilters.tsx" />
                  <DatePickerInput value={filters.maxDate} onValueChange={date => setFilters(filters => ({
                  ...filters,
                  maxDate: date
                }))} label={t("label_dateTo_20230807145098")} className="w-full rounded-xl" data-sentry-element="DatePickerInput" data-sentry-source-file="PublicAquisitionsFilters.tsx" />
                </div>
              </Card>
              <Card className="flex flex-col gap-4 rounded-3xl p-6" data-sentry-element="Card" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                <span>
                  {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_3")}
                </span>
                <div className="flex flex-col gap-4 desktop:flex-row">
                  <LocaleNumberInput placeholder={t("placeholder_valueFrom_20230807145006")} value={filters.minValue} onChange={newValue => setFilters(filters => ({
                  ...filters,
                  minValue: newValue
                }))} data-sentry-element="LocaleNumberInput" data-sentry-source-file="PublicAquisitionsFilters.tsx" />
                  <LocaleNumberInput placeholder={t("placeholder_valueTo_20230807145007")} value={filters.maxValue} onChange={newValue => setFilters(filters => ({
                  ...filters,
                  maxValue: newValue
                }))} data-sentry-element="LocaleNumberInput" data-sentry-source-file="PublicAquisitionsFilters.tsx" />
                </div>
              </Card>
              <Card className="flex flex-col gap-4 rounded-3xl p-6" data-sentry-element="Card" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                <span>
                  {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_4")}
                </span>
                <div className="flex gap-4">
                  <Select value={filters.procedureType} onValueChange={value => setFilters(filters => ({
                  ...filters,
                  procedureType: value as string
                }))} key={filters.procedureType} data-sentry-element="Select" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                    <SelectTrigger className="w-full rounded-xl" onClear={filters.procedureType ? () => setFilters(filters => ({
                    ...filters,
                    procedureType: undefined
                  })) : undefined} data-sentry-element="SelectTrigger" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                      {filters.procedureType ? <SelectValue /> : <div className="text-muted-foreground">
                          <span>
                            {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_5")}
                          </span>
                        </div>}
                    </SelectTrigger>
                    <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                      {PUBLIC_AQUISITION_STATUS.map(status => <SelectItem key={status} value={status}>
                          {i18nStatus(status)}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </Card>
              <Card className="flex flex-col gap-4 rounded-3xl p-6" data-sentry-element="Card" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                <span>
                  {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_6")}
                </span>
                <Select value={filters.type as string} onValueChange={value => setFilters(filters => ({
                ...filters,
                type: value as (typeof PUBLIC_AQUISITIONS_TYPE)[number]
              }))} key={filters.type} data-sentry-element="Select" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                  <SelectTrigger className="w-full rounded-xl" onClear={filters.type ? () => setFilters(filters => ({
                  ...filters,
                  type: undefined
                })) : undefined} data-sentry-element="SelectTrigger" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                    {filters.type ? <SelectValue>{i18nType(filters.type)}</SelectValue> : <div className="text-muted-foreground">
                        <span>
                          {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_7")}
                        </span>
                      </div>}
                  </SelectTrigger>
                  <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                    <SelectItem value={PUBLIC_AQUISITIONS_PUBLIC} data-sentry-element="SelectItem" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                      {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_8")}
                    </SelectItem>
                    <SelectItem value={PUBLIC_AQUISITIONS_DIRECT} data-sentry-element="SelectItem" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                      {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_9")}
                    </SelectItem>
                    <SelectItem value={PUBLIC_AQUISITIONS_OFFLINE} data-sentry-element="SelectItem" data-sentry-source-file="PublicAquisitionsFilters.tsx">
                      {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_10")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Card>
            </div>
            <Button onClick={onSave} data-sentry-element="Button" data-sentry-source-file="PublicAquisitionsFilters.tsx">
              {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionsFilters_tsx_0_2")}
            </Button>
          </DialogContent>
        </Dialog>
      </div>
      <div className="flex w-full flex-col items-center justify-center gap-4 desktop:flex-row desktop:justify-end">
        <Input className="w-full max-w-md rounded-3xl bg-background px-4" containerClassName="w-full h-10" placeholder={t("search_placeholder_20230802123503")} value={searchKeywords} onChange={e => setSearchKeywords(e.target.value)} data-sentry-element="Input" data-sentry-source-file="PublicAquisitionsFilters.tsx" />
      </div>
    </div>;
};
export default PublicAquisitionsFilters;